import { getHomePagePerPermissions } from './permissions-utils'
import { type Environment, type Role, type User } from './types/user'

export function getEnvironment(): Environment {
  const backendEnvironment = import.meta.env.VITE_API_URL

  if (backendEnvironment.includes('platform')) return 'platform'
  if (backendEnvironment.includes('demo')) return 'demo'
  return 'testing'
}

export const getPlatform = (env: Environment) =>
  `https://${env}.povertystoplight.org`

export const NEW = 'new'
// Used to mark access to a new view
export const ACCESS = 'access'

// IMPORTANT: Add new roles by their hierarchy otherwise getRolesForFilter won't work properly
export const ROLES = {
  ADMIN: 'ROLE_ROOT',
  PS_TEAM: 'ROLE_PS_TEAM',
  HUB_ADMIN: 'ROLE_HUB_ADMIN',
  STAKEHOLDER: 'ROLE_STAKEHOLDER',
  ORGANIZATION_ADMIN: 'ROLE_APP_ADMIN',
  FACILITATOR_ADMIN: 'ROLE_SURVEY_USER_ADMIN',
  FACILITATOR: 'ROLE_SURVEY_USER',
  SURVEY_TAKER: 'ROLE_SURVEY_TAKER',
  FAMILY: 'ROLE_FAMILY_USER',
} as const

export const ROLES_MAP: Record<
  (typeof ROLES)[keyof typeof ROLES],
  Array<{ item: string; shouldShowOnHeader: boolean; options?: string[] }>
> = {
  [ROLES.ADMIN]: [
    {
      item: 'homepage',
      shouldShowOnHeader: false,
    },
    {
      item: 'analytics',
      shouldShowOnHeader: true,
      options: ['dashboard', 'reports', 'superset'],
    },
    {
      item: 'allies',
      shouldShowOnHeader: true,
      options: ['hubs', 'stakeholders'],
    },
    { item: 'organizations', shouldShowOnHeader: false },
    { item: 'projects', shouldShowOnHeader: false },
    { item: 'surveysList', shouldShowOnHeader: true },
    {
      item: 'users',
      shouldShowOnHeader: true,
      options: ['users', 'user-invitation'],
    },
    { item: 'families', shouldShowOnHeader: true },
    { item: 'detail', shouldShowOnHeader: false },
    { item: 'map', shouldShowOnHeader: true },
    { item: 'solutions', shouldShowOnHeader: true },
    { item: 'editFamilyImages', shouldShowOnHeader: false },
    { item: 'offline-maps', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
    { item: 'survey-builder', shouldShowOnHeader: false },
    {
      item: 'libraries',
      shouldShowOnHeader: true,
      options: ['dimensions', 'indicators', 'economics', 'codenames'],
    },
  ],
  [ROLES.STAKEHOLDER]: [],
  [ROLES.HUB_ADMIN]: [
    {
      item: 'homepage',
      shouldShowOnHeader: false,
    },
    {
      item: 'analytics',
      shouldShowOnHeader: true,
      options: ['dashboard', 'reports', 'superset'],
    },
    { item: 'projects', shouldShowOnHeader: false },
    { item: 'surveysList', shouldShowOnHeader: true },
    { item: 'organizations', shouldShowOnHeader: true },
    { item: 'organizationEdit', shouldShowOnHeader: false },
    { item: 'users', shouldShowOnHeader: true },
    { item: 'families', shouldShowOnHeader: true },
    { item: 'detail', shouldShowOnHeader: false },
    { item: 'map', shouldShowOnHeader: true },
    { item: 'solutions', shouldShowOnHeader: false },
    { item: 'offline-maps', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
    { item: 'interventions', shouldShowOnHeader: true },
    { item: 'survey-builder', shouldShowOnHeader: false },
  ],
  [ROLES.PS_TEAM]: [
    {
      item: 'homepage',
      shouldShowOnHeader: false,
    },
    {
      item: 'analytics',
      shouldShowOnHeader: true,
      options: ['dashboard', 'reports', 'superset'],
    },
    {
      item: 'allies',
      shouldShowOnHeader: true,
      options: ['hubs', 'stakeholders'],
    },
    {
      item: 'users',
      shouldShowOnHeader: true,
      options: ['users', 'user-invitation'],
    },
    { item: 'surveysList', shouldShowOnHeader: true },
    { item: 'map', shouldShowOnHeader: true },
    { item: 'organizations', shouldShowOnHeader: false },
    { item: 'projects', shouldShowOnHeader: false },
    { item: 'solutions', shouldShowOnHeader: false },
    { item: 'editFamilyImages', shouldShowOnHeader: false },
    { item: 'offline-maps', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
    { item: 'survey-builder', shouldShowOnHeader: false },
    {
      item: 'libraries',
      shouldShowOnHeader: true,
      options: ['dimensions', 'indicators', 'economics', 'codenames'],
    },
  ],
  [ROLES.ORGANIZATION_ADMIN]: [
    {
      item: 'homepage',
      shouldShowOnHeader: false,
    },
    {
      item: 'analytics',
      shouldShowOnHeader: true,
      options: ['dashboard', 'reports'],
    },
    { item: 'projects', shouldShowOnHeader: true },
    { item: 'surveysList', shouldShowOnHeader: true },
    {
      item: 'users',
      shouldShowOnHeader: true,
      options: ['users', 'user-invitation'],
    },
    { item: 'families', shouldShowOnHeader: true },
    { item: 'detail', shouldShowOnHeader: false },
    { item: 'map', shouldShowOnHeader: true },
    { item: 'solutions', shouldShowOnHeader: false },
    { item: 'editFamily', shouldShowOnHeader: false },
    { item: 'editFamilyImages', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
    { item: 'survey-builder', shouldShowOnHeader: false },
    { item: 'massive-interventions', shouldShowOnHeader: true },
  ],
  [ROLES.FACILITATOR_ADMIN]: [
    {
      item: 'homepage',
      shouldShowOnHeader: false,
    },
    {
      item: 'analytics',
      shouldShowOnHeader: true,
      options: ['dashboard', 'reports'],
    },
    { item: 'surveys', shouldShowOnHeader: true },
    { item: 'families', shouldShowOnHeader: true },
    { item: 'priorities', shouldShowOnHeader: false },
    { item: 'users', shouldShowOnHeader: true },
    { item: 'detail', shouldShowOnHeader: false },
    { item: 'map', shouldShowOnHeader: true },
    { item: 'editFamily', shouldShowOnHeader: false },
    { item: 'editFamilyImages', shouldShowOnHeader: false },
    { item: 'solutions', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
    { item: 'massive-interventions', shouldShowOnHeader: true },
  ],
  [ROLES.FACILITATOR]: [
    {
      item: 'homepage',
      shouldShowOnHeader: false,
    },
    { item: 'surveys', shouldShowOnHeader: true },
    {
      item: 'analytics',
      shouldShowOnHeader: true,
      options: ['dashboard'],
    },
    { item: 'families', shouldShowOnHeader: true },
    { item: 'priorities', shouldShowOnHeader: false },
    { item: 'detail', shouldShowOnHeader: false },
    { item: 'map', shouldShowOnHeader: true },
    { item: 'editFamily', shouldShowOnHeader: false },
    { item: 'editFamilyImages', shouldShowOnHeader: false },
    { item: 'solutions', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
  ],
  [ROLES.SURVEY_TAKER]: [
    {
      item: 'surveys',
      shouldShowOnHeader: true,
      options: ['surveys', 'dynamic-links'],
    },
    { item: 'support', shouldShowOnHeader: false },
  ],
  [ROLES.FAMILY]: [
    { item: 'my-profile', shouldShowOnHeader: true },
    { item: 'detail', shouldShowOnHeader: false },
    { item: 'priorities', shouldShowOnHeader: false },
    { item: 'achievements', shouldShowOnHeader: false },
    { item: 'support', shouldShowOnHeader: false },
    { item: 'solutions', shouldShowOnHeader: false },
  ],
}

/**
 * Check if a user's role is allowed to a certain module
 */
export function checkAccess(user: User | null, item: string) {
  if (!user) return false
  return (
    !!user.role &&
    !!ROLES_MAP[user.role] &&
    !!ROLES_MAP[user.role].find(
      r => r.item === item || r.options?.find(o => o === item),
    )
  )
}

export function checkAccessToSolution(user: User) {
  const hasPermission =
    (!user.permissions && checkAccess(user, 'solutions')) ||
    (user.permissions && !!user.permissions.some(p => p === 'solutions_read'))

  if (
    hasPermission &&
    !user.hub &&
    !!user.organization &&
    !user.organization.id
  )
    return true
  else if (
    hasPermission &&
    !!user.hub &&
    user.hub.allowSolutions &&
    !!user.organization &&
    !user.organization.id
  )
    return true
  else if (
    hasPermission &&
    user?.organization?.application?.allowSolutions &&
    user?.organization?.solutionsAccess !== 'NONE'
  ) {
    if (user.role === ROLES.ORGANIZATION_ADMIN) {
      return true
    }

    if (user.role === ROLES.FAMILY) {
      return true
    }

    if (
      (user.role === ROLES.FACILITATOR_ADMIN ||
        user.role === ROLES.FACILITATOR) &&
      user.organization?.solutionsAllowedFacilitators
    ) {
      return true
    }
  }

  return false
}

export function canAccess(
  user: User | null,
  item: string, // stakeholder
  typePerm?: string,
) {
  let granted = false

  if (!user) return granted

  if (Array.isArray(user.permissions) && item) {
    const permission = typePerm ? `${item}_${typePerm}` : `${item}_read`
    granted = user.permissions.some(p => p === permission)
  }

  if (!Array.isArray(user.permissions)) {
    granted =
      !!user.role &&
      !!ROLES_MAP[user.role].find(
        r => r.item === item || r.options?.find(o => o === item),
      )
  }

  if (item === 'massive-interventions') {
    granted =
      user.role === ROLES.ADMIN ||
      user.role === ROLES.FACILITATOR_ADMIN ||
      user.role === ROLES.ORGANIZATION_ADMIN
  }

  if (item === 'solutions') {
    granted = granted && checkAccessToSolution(user)
  }

  if (item === 'projects') {
    // TODO:  Avoid special condition por surveys users
    granted =
      (granted && checkAccessToProjects(user)) ||
      (!user.permissions &&
        (user.role === ROLES.FACILITATOR ||
          user.role === ROLES.FACILITATOR_ADMIN))
  }
  return granted
}

export function getHomePage(user: User): string {
  if (user.permissions) {
    return getHomePagePerPermissions(user.permissions)
  }
  return ROLES_MAP[user.role][0].item
}

export function checkAccessToProjects(user: User) {
  const hasPermission =
    (!user.permissions && checkAccess(user, 'projects')) ||
    (user.permissions &&
      !!user.permissions.some(
        p => p === 'projects_read' || p === 'projects_view',
      ))

  if (
    hasPermission &&
    !user.hub &&
    !!user.organization &&
    !user.organization.id &&
    !user.stakeholder
  )
    return true
  else if (
    hasPermission &&
    !!user.hub &&
    user.hub.projectsSupport &&
    !!user.organization &&
    !user.organization.id
  )
    return true
  else if (
    hasPermission &&
    !!user.organization &&
    !!user.organization.projectsAccess &&
    ((!!user.organization.application &&
      !!user.organization.application.projectsSupport) ||
      (!!user.hub && user.hub.projectsSupport))
  ) {
    return true
  }

  return false
}

export function checkAccessToHubLabel(user: User | null, label: string) {
  if (!user) return false

  const applicationLabels = user.organization?.application?.labels
  if (Array.isArray(applicationLabels) && applicationLabels.includes(label)) {
    return true
  }

  return (
    !!user.hub &&
    Array.isArray(user.hub.labels) &&
    user.hub.labels.includes(label)
  )
}

export function checkAccessToChatbot(user: User | null) {
  if (!user) return false
  if (user.role === ROLES.FACILITATOR || user.role === ROLES.FACILITATOR_ADMIN)
    return false

  return (
    user.role === ROLES.ADMIN ||
    user.role === ROLES.PS_TEAM ||
    checkAccessToHubLabel(user, 'chatbotEnabled')
  )
}

export function checkAccessToSuperset(user: User | null) {
  if (!user) return false
  if (user.role === ROLES.ADMIN || user.role === ROLES.PS_TEAM) return true
  if (user.role === ROLES.HUB_ADMIN)
    return checkAccessToHubLabel(user, 'supersetEnabled')
  return false
}

export function checkAccessToInterventions(user: User | null) {
  if (!user) return false
  else if (
    user.role === ROLES.ADMIN ||
    user.role === ROLES.PS_TEAM ||
    user.role === ROLES.FACILITATOR_ADMIN
  )
    return true
  else if (
    !!user.hub &&
    Array.isArray(user.hub.labels) &&
    user.hub.labels.includes('allowInterventions')
  )
    return true
  return false
}

export function getVisibleRoles(user: User, shouldReturnAllRoles = false) {
  const roles: Role[] = []
  if (user.role === ROLES.ADMIN) {
    roles.push(ROLES.HUB_ADMIN)
    roles.push(ROLES.PS_TEAM)
    roles.push(ROLES.STAKEHOLDER)
  } else if (user.role === ROLES.PS_TEAM) {
    roles.push(ROLES.HUB_ADMIN)
    roles.push(ROLES.STAKEHOLDER)
  } else if (user.role === ROLES.HUB_ADMIN) {
    roles.push(ROLES.ORGANIZATION_ADMIN)
  } else if (user.role === ROLES.ORGANIZATION_ADMIN) {
    roles.push(ROLES.FACILITATOR_ADMIN)
    roles.push(ROLES.FACILITATOR)
    roles.push(ROLES.SURVEY_TAKER)
    //* NOTE: This will be removed until the Family User is used again
    //* Check: https://povertystoplight.atlassian.net/browse/PSP-1846
    // if (checkAccessToFamilyUsers(user)) roles.push(ROLES.FAMILY)
  } else if (user.role === ROLES.FACILITATOR_ADMIN) {
    roles.push(ROLES.FACILITATOR)
    roles.push(ROLES.SURVEY_TAKER)
  }

  const allRoles = Object.keys(ROLES) as Role[]
  return shouldReturnAllRoles ? allRoles : roles
}

/**
 * @deprecated This function is really useless, try to move this log to the <RolesSelector /> component
 */
export function getRolesForFilter() {
  const roles = Object.keys(ROLES) as Array<keyof typeof ROLES>
  return roles
}
